/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SearchFilter } from '../../../shared/interfaces/search-filter';
import { environment } from '../../../../environments/environment';
import { SearchDetailFilter } from '../../../shared/interfaces/search-detail-filter';
import { DestroyBase } from '../../../shared/base-classes/destroy-component-base';
import { SearchResponse } from '../models/search-response';
import { CompanyDetails } from '../../../shared/models/company-details';
import { PaginatedResponse } from '../../../shared/interfaces/pagination';
import { ShareRecord } from '../models/share-record';
import { SourceTypeConverter } from '../models/search-source-types';
import { RecordLeadsHistory } from '../models/record-leads-history';
import { LeadNote } from '../../leads/models/lead-note';
import { RecordScore } from '../../../shared/interfaces/record-score';
import {
  Connection,
  ConnectionWithSharedNote,
} from '../../outreach/models/connection.model';
import { isNullOrUndefined } from '../../../shared/utils/utils';
import { RecordCount } from '../models/record-count';
import {
  CheckRecordExistenceRequest,
  RecordMatch,
} from '../models/check-record-existence';

@Injectable({
  providedIn: 'root',
})
export class SearchService extends DestroyBase {
  private apiUrl = environment.apiBaseUrl.endsWith('/')
    ? environment.apiBaseUrl.trimEnd()
    : environment.apiBaseUrl;
  private http: HttpClient = inject(HttpClient);

  getCompanies(
    searchFilter: SearchFilter,
    page = 1,
    pageSize = 10,
    skipLoader = false,
  ): Observable<SearchResponse> {
    let params = new HttpParams();

    let body: any = {};
    if (searchFilter.data_axle_search) {
      body = {
        ...searchFilter.data_axle_search,
        sources: searchFilter.sources
          ? SourceTypeConverter.getApiSources(searchFilter.sources)
          : undefined,
      };
    } else if (searchFilter.advanced_search) {
      body = {
        ...searchFilter.advanced_search_filter,
        sources: searchFilter.sources
          ? SourceTypeConverter.getApiSources(searchFilter.sources)
          : undefined,
      };
    } else {
      body = {
        ...searchFilter,
      };

      if (searchFilter.sources) {
        body.sources = SourceTypeConverter.getApiSources(searchFilter.sources);
      }
    }

    params = params.set('page', page.toString());
    params = params.set('page_size', pageSize.toString());

    let url = `${this.apiUrl}/api/records`;

    if (!isNullOrUndefined(searchFilter.advanced_search)) {
      url += `?advanced_search=${searchFilter.advanced_search}`;
    }

    return this.http.post<SearchResponse>(url, body, {
      params,
      headers: {
        'X-Skip-Loader': skipLoader ? 'true' : 'false',
      },
    });
  }

  getCompanyDetails(
    searchFilter: SearchDetailFilter,
  ): Observable<CompanyDetails> {
    let params = new HttpParams();

    if (searchFilter.source) {
      params = params.set('source', searchFilter.source);
    }

    if (searchFilter.nocache) {
      params = params.set('nocache', searchFilter.nocache);
    }

    return this.http.get<CompanyDetails>(
      `${this.apiUrl}/api/record/${searchFilter.unique_id}/detail`,
      { params },
    );
  }

  checkRecordExistence(
    businessName?: string,
    email?: string,
    page = 1,
    pageSize = 10,
  ): Observable<PaginatedResponse<RecordMatch>> {
    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('page_size', pageSize.toString());

    const body: CheckRecordExistenceRequest = {};
    if (businessName) {
      body.business_name = businessName;
    }
    if (email) {
      body.email = email;
    }

    return this.http.post<PaginatedResponse<RecordMatch>>(
      `${this.apiUrl}/api/record/check-existence`,
      body,
      {
        params,
        headers: {
          'X-Skip-Loader': 'true',
        },
      },
    );
  }

  claimVlfRecord(uniqueId: string): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/api/record/vlf/${uniqueId}/claim`,
      {},
    );
  }

  getVlfRecordDetail(uniqueId: string): Observable<CompanyDetails> {
    return this.http.get<CompanyDetails>(
      `${this.apiUrl}/api/record/vlf/${uniqueId}`,
    );
  }

  getVlfStatus(): Observable<any> {
    return this.http.get(`${this.apiUrl}/api/vlf/status`);
  }

  updateVlfStatus(status: boolean): Observable<any> {
    return this.http.put(`${this.apiUrl}/api/vlf/status`, { status });
  }

  saveBusinessRecord(payload: any, source: string): Observable<any> {
    const params = new HttpParams().set('source', source);
    return this.http.post(`${this.apiUrl}/api/record/`, payload, { params });
  }

  deleteCompanyDetails(uniqueId: string, source: string): Observable<any> {
    const params = new HttpParams().set('source', source);
    return this.http.delete(`${this.apiUrl}/api/record/${uniqueId}/detail`, {
      params,
    });
  }

  updateCompanyDetails(data: any, source: string): Observable<CompanyDetails> {
    const params = new HttpParams().set('source', source);
    return this.http.patch<CompanyDetails>(
      `${this.apiUrl}/api/record/${data.unique_id}/detail`,
      data,
      { params },
    );
  }

  getRecordConnections(
    recordUniqueId: string,
    searchKey: string,
    page = 1,
    pageSize = 10,
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams();
    if (searchKey) {
      params = params.set('search_key', searchKey);
    }
    params = params.set('page', page.toString());
    params = params.set('page_size', pageSize.toString());

    return this.http.get<PaginatedResponse<any>>(
      `${this.apiUrl}/api/record/${recordUniqueId}/share/connections`,
      { params },
    );
  }

  getRecordConnectionUsers(
    recordUniqueId: string,
    connectionId: number,
    page = 1,
    pageSize = 10,
    searchKey = '',
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('page_size', pageSize.toString());

    if (searchKey) {
      params = params.set('search_key', searchKey);
    }

    return this.http.get<PaginatedResponse<any>>(
      `${this.apiUrl}/api/record/${recordUniqueId}/share/connections/${connectionId}/users`,
      { params },
    );
  }

  shareRecord(
    shareRecord: ShareRecord,
    unique_id: string,
  ): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/api/record/${unique_id}/share`,
      shareRecord,
    );
  }

  getRecordLeadsHistory(uniqueId: string): Observable<RecordLeadsHistory[]> {
    return this.http.get<RecordLeadsHistory[]>(
      `${this.apiUrl}/api/record/${uniqueId}/leads/archive`,
    );
  }

  createRecordNote(uniqueId: string, note: string): Observable<LeadNote> {
    return this.http.post<LeadNote>(
      `${this.apiUrl}/api/record/${uniqueId}/notes`,
      { note },
    );
  }

  updateRecordNote(
    uniqueId: string,
    noteId: string,
    note: string,
  ): Observable<LeadNote> {
    return this.http.patch<LeadNote>(
      `${this.apiUrl}/api/record/${uniqueId}/notes/${noteId}`,
      { note },
    );
  }

  deleteRecordNote(uniqueId: string, noteId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/api/record/${uniqueId}/notes/${noteId}`,
    );
  }

  getRecordNotes(uniqueId: string): Observable<LeadNote[]> {
    return this.http.get<LeadNote[]>(
      `${this.apiUrl}/api/record/${uniqueId}/notes`,
    );
  }

  getRecordNoteById(uniqueId: string, noteId: string): Observable<LeadNote> {
    return this.http.get<LeadNote>(
      `${this.apiUrl}/api/record/${uniqueId}/notes/${noteId}`,
    );
  }

  getRecordLoanScore(
    uniqueId: string,
    custom_data = false,
    source?: string,
  ): Observable<RecordScore> {
    const isSmeRecord = source === 'SME';

    return this.http.get<RecordScore>(
      `${this.apiUrl}/api/record/${uniqueId}/score?custom_data=${isSmeRecord ? true : custom_data}`,
    );
  }

  getAssignedRecordConnections(
    uniqueId: string,
  ): Observable<ConnectionWithSharedNote[]> {
    return this.http.get<ConnectionWithSharedNote[]>(
      `${this.apiUrl}/api/record/${uniqueId}/leads`,
    );
  }

  shareNote(connections: any[], noteId: number): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/api/record/notes/${noteId}/share`, {
      connection_ids: connections,
    });
  }

  getMatchedConnectionsWithLoanAmount(
    uniqueId: string,
  ): Observable<Connection[]> {
    return this.http.get<Connection[]>(
      `${this.apiUrl}/api/record/${uniqueId}/match-loan-amount`,
      {
        headers: {
          'X-Skip-Loader': 'true',
        },
      },
    );
  }

  getRecordArcgisIntersectionData(uniqueId: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/api/record/${uniqueId}/arcgis/intersect`,
      {
        headers: {
          'X-Skip-Loader': 'true',
        },
      },
    );
  }

  getRecordCount(): Observable<RecordCount> {
    return this.http.get<RecordCount>(`${this.apiUrl}/api/record/count`, {
      headers: {
        'X-Skip-Loader': 'true',
      },
    });
  }

  updateLeadAttributes(
    recordId: string,
    payload: Record<string, any>,
  ): Observable<any> {
    return this.http.patch(
      `${this.apiUrl}/api/record/${recordId}/lead-attributes`,
      payload,
    );
  }
}
